<template>
    <div class="px-4 pt-14 pb-4">

        <div class="pb-4 bg-white dark:bg-neutral-800 max-w-full">
            <label for="table-search" class="sr-only">Search</label>
            <div class="relative mt-1">
                <div class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none">
                    <svg class="w-4 h-4 text-gray-500 dark:text-neutral-400" aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                    </svg>
                </div>
                <input type="text" id="table-search" v-model="search" @input="handleSearch"
                    class="block pt-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 dark:bg-neutral-800 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white max-w-full"
                    :placeholder="$t('Search for your events')">
            </div>
        </div>

        <div v-if="loading" class="flex justify-center items center">
            <div class="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
                <svg class="text-gray-300 animate-spin" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"
                    width="24" height="24">
                    <path
                        d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                        stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path
                        d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                        stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"
                        class="text-gray-900">
                    </path>
                </svg>
            </div>
        </div>

        <ul v-else>
            <div class="relative space-y-3">
                <table class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-neutral-400 table-fixed">
                    <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-neutral-800 dark:text-neutral-400">
                        <tr>
                            <th scope="col" class="px-6 py-3 w-1/2">
                                {{ $t('Name') }}
                            </th>
                            <th scope="col" class="px-6 py-3 !text-right">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in items.data" :key="item.id"
                            class="bg-white border-b dark:bg-neutral-800 dark:border-neutral-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-600">
                            <th scope="row" class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white truncate w-1/2">
                                {{ getTranslated(item.resource?.name) }}
                            </th>
                            <td class="!px-6 !py-4 flex gap-2 items-center justify-end">
                                <div v-if="showItem(item.resource)" class="flex gap-2 items-center justify-end">
                                    <a :href="$baseUrl + '/events/' + item.resource?.slug" target="_blank"
                                        class="font-medium text-gray-500 hover:text-primary cursor-pointer dark:text-neutral-500">
                                        <svg-vue icon="eye" class="w-4 h-4"></svg-vue>
                                    </a>

                                    <!-- <a href="#" class="font-medium text-gray-500 hover:text-primary cursor-pointer dark:text-neutral-500">
                                        <svg-vue icon="pen" class="w-4 h-4"></svg-vue>
                                    </a> -->
                                </div>
                                <span v-else
                                    class="inline-flex items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 inset-ring inset-ring-gray-400/20">
                                    {{ $t('Unavailable') }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>


                <div class="flex justify-between items-center gap-x-1" aria-label="Pagination">
                    <button type="button" :disabled="!items.prev_page_url" @click="changePage(items.current_page - 1)"
                        class="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-gray-800 hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:bg-white/10 dark:focus:bg-white/10"
                        aria-label="Previous">
                        <svg class="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round">
                            <path d="m15 18-6-6 6-6"></path>
                        </svg>
                        <span aria-hidden="true" class="hidden sm:block">Previous</span>
                    </button>
                    <div class="flex items-center gap-x-1">
                        <span
                            class="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm dark:text-neutral-500">{{
                                items.current_page }} </span>
                        <span
                            class="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm dark:text-neutral-500">of</span>
                        <span
                            class="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm dark:text-neutral-500">{{
                                items.last_page }} </span>
                    </div>
                    <button type="button" :disabled="!items.next_page_url" @click="changePage(items.current_page + 1)"
                        class="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-gray-800 hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:bg-white/10 dark:focus:bg-white/10">
                        <span aria-hidden="true" class="hidden sm:block">Next</span>
                        <svg class="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round">
                            <path d="m9 18 6-6-6-6"></path>
                        </svg>
                    </button>
                </div>
            </div>

        </ul>
    </div>
</template>
    
<script>
import dashboardHelpers from "../dashboard-helpers";
import debounce from "lodash/debounce";
export default {
    props: ["resource"],
    data() {
        return { loading: false, items: [], search: "" }
    },
    async mounted() {
        this.fetchItems()
    },
    methods: {
        ...dashboardHelpers,
        async fetchItems(page = 1) {
            try {
                this.loading = true;

                const res = await axios.get("/api/dashboard/events", {
                    params: {
                        search: this.search || "",
                        page: page
                    }
                });

                this.items = res.data;
            } catch (error) {
                if (error.response?.status === 401) {
                    // User is unauthenticated → refresh token or redirect to login
                    console.warn('User unauthenticated. Attempting refresh...');
                    window.location.reload();
                } else {
                    console.error('Error fetching events:', error);
                }
            } finally {
                this.loading = false;
            }
        },

        changePage(page) {
            this.fetchItems(page);
        },

        // ✅ Debounce search call
        handleSearch: debounce(function () {
            this.fetchItems()
        }, 300),
        showItem(resource) {
            if (!resource || !resource.clients) return false;
            const clientIds = resource.clients.map(client => client.id);
            return clientIds.includes(this.$clientID);
        }
    }
}
</script>
    